import React, { FC, useState, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'

import { useTranslation } from 'react-i18next'
import Modal from '../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { combineYearMonthAndDay, format, nowDate, prevMonth } from '../../../utils/date'
import { useParams } from 'react-router-dom'
import { LangValue } from '../../../states/types/common'
import { sortBy } from 'lodash'
import {
  fetchTaskExportList,
  fetchTaskViewableLabelGroup,
  updateTaskExportDetail,
} from '../../../states/containers/task/taskSlice'
import { getZendeskToken } from '../../../states/apis/auth'
import { setErrors } from '../../../states/containers/common/errorsSlice'
import { openNewTab } from '../../../utils/url'
import { compile } from '../../../utils/search'
import LoadingSquare from '../common/LoadingSquare'

interface ExportModalState {
  display: boolean,
  type: string,
  id: number | undefined,
}

interface LabelGroup {
  label_group_id: number
  label_group_name: string
  label_group_color: string
  label_group_code?: string | null
}

interface Props {
  exportModalState: ExportModalState
  closeCallback: VoidFunction
}

const SEARCH_LIMIT = 1000

const ExportModal: FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    commonPart,
    task,
    login,
  } = useSelector((state: RootState) => state)

  const {
    exportModalState,
    closeCallback,
  } = props

  const initialInput = {
    type: "project",
    code: "Shift-JIS",
    is_only_approved: true,
    labelGroupId: 0,
    targetYear: Number(`${format(prevMonth(nowDate()), "YYYY")}`),
    targetDate: `${format(prevMonth(nowDate()), "YYYYMM", language)}`,
  }

  const [inputCode, setInputCode] = useState<string>(initialInput.code)
  const [inputIsApproved, setInputIsApproved] = useState<boolean>(initialInput.is_only_approved)
  const [inputLabelGroupId, setInputLabelGroupId] = useState<number>(initialInput.labelGroupId)
  const [targetYear, setTargetYear] = useState<number>(initialInput.targetYear)
  const [targetDate, setTargetDate] = useState<string>(initialInput.targetDate)

  const [fileType, setFileType] = useState<string>(initialInput.type)

  const [displayDate, setDisplayDate] = useState<string>(
    combineYearMonthAndDay(
      `${String(initialInput.targetYear)}${(`0${prevMonth(nowDate(), 'MM')}`).slice(-2)}`, 1, t('time.date.formats.month_year'), language,
    ),
  )

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [exportType, setExportType] = useState<string>("label_group")

  const [labelGroupListInit, setLabelGroupListInit] = useState<LabelGroup[]>([])
  const [labelGroupList, setLabelGroupList] = useState<LabelGroup[]>([])
  const [searchLabelGroupWord, setSearchLabelGroupWord] = useState<string>("")
  const [selectLabelGroup, setSelectLabelGroup] = useState<LabelGroup>()

  const [isWordLoading, setIsWordLoading] = useState<boolean>(false)
  const [composition, setComposition] = useState<boolean>(false)
  const [isSearched, setIsSearched] = useState<boolean>(false)

  const [isDisplaySelectLabelGroupModal, setIsDisplaySelectLabelGroupModal] = useState<boolean>(false)

  const month = t('time.date.formats.monthName').split(' ').map((v) => v)

  const openCalendarModal = () => {
    setIsCalendarDisplay(true)
  }

  const closeCalendarModal = () => {
    setIsCalendarDisplay(false)
  }

  const handleChangeMonth = (value: string) => {
    setIsCalendarDisplay(false)
    setTargetDate(`${String(targetYear)}${value.slice(-2)}`)
    setDisplayDate(
      combineYearMonthAndDay(`${String(targetYear)}${value.slice(-2)}`, 1, t('time.date.formats.month_year'), language),
    )
  }

  const updateLabelGroupId = (id: number) => {
    setInputLabelGroupId(id)
    setTargetYear(initialInput.targetYear)
    setTargetDate(initialInput.targetDate)
    setDisplayDate(combineYearMonthAndDay(initialInput.targetDate, 1, t('time.date.formats.month_year'), language))
  }

  const saveExport = () => {
    if (!commonPart.company?.id) return
    const columns = []
    if (exportType === "label_group") {
      columns.push("label_group")
    } else if (exportType === "label") {
      columns.push("label_group", "label")
    } else if (exportType === "member") {
      columns.push("label_group", "label", "member")
    } else if (exportType === "date") {
      columns.push("label_group", "label", "member", "date")
    }
    dispatch(
      updateTaskExportDetail(
        {
          file_type: fileType,
          label_group_id: fileType === "project" ? inputLabelGroupId : null,
          target_date: format(targetDate, "YYYY-MM-01"),
          code: inputCode,
          is_only_approved: inputIsApproved,
          columns: fileType === "category" ? columns : [],
        },
        () => {
          alert.success(
            <>
              {t('export.alert.start')}
            </>,
          )
          dispatch(fetchTaskExportList())
          closeCallback()
        },
      ),
    )
  }

  const clickZendeskLink = (url: string) => {
    getZendeskToken()
      .then((result) => {
        openNewTab(`${result.url}&return_to=${url}`, 'post')
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }

  const resetLabelGroup = () => {
    setSearchLabelGroupWord("")
    setLabelGroupList(labelGroupListInit)
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }
  const searchLabelGroupList = (word?: string) => {
    let words: string[] = []
    if (word !== undefined) {
      words = word.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    } else {
      words = searchLabelGroupWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    }
    if (!words.length) {
      resetLabelGroup()
      return
    }
    if (login.permissions?.high_volume) {
      setIsWordLoading(true)
      dispatch(
        fetchTaskViewableLabelGroup(
          {
            keywords: words,
            limit: SEARCH_LIMIT,
          },
          (result) => {
            setLabelGroupList(result.label_groups.map((v) => ({
              label_group_id: v.id,
              label_group_name: v.name,
              label_group_code: v.code,
              label_group_color: v.color,
            })))
            setIsWordLoading(false)
            setIsSearched(true)
          },
        ),
      )
      return
    }
    const list = labelGroupListInit.filter((labelGroup) => {
      if (labelGroup === null) return false
      return (compile(words).test(labelGroup.label_group_name.toLowerCase()))
    })
    setLabelGroupList(sortBy(list, 'id'))
    setIsSearched(true)
  }

  const inputSearchWord = (word: string) => {
    setSearchLabelGroupWord(word)
    if (!login.permissions?.high_volume) searchLabelGroupList(word)
  }

  const clickLabelGroup = (labelGroup: LabelGroup) => {
    setSelectLabelGroup(labelGroup)
    setInputLabelGroupId(labelGroup.label_group_id)
    setIsDisplaySelectLabelGroupModal(false)
    inputSearchWord("")
  }

  useEffect(() => {
    if (!exportModalState || !task.taskExport || !task.taskExport.label_groups.length) return
    setInputCode(initialInput.code)
    setInputIsApproved(initialInput.is_only_approved)
    setSelectLabelGroup(task.taskExport?.label_groups[0])
    updateLabelGroupId(task.taskExport?.label_groups[0].label_group_id)
    setTargetYear(initialInput.targetYear)
    setTargetDate(initialInput.targetDate)
    setDisplayDate(
      combineYearMonthAndDay(
        `${String(initialInput.targetYear)}${(`0${prevMonth(nowDate(), 'MM')}`).slice(-2)}`, 1, t('time.date.formats.month_year'), language,
      ),
    )
  }, [exportModalState])

  useEffect(() => {
    setDisplayModal(exportModalState.display)
    setIsCalendarDisplay(false)
    setFileType(login.permissions?.task_report_view ? "project" : "attendance")
    setIsDisplaySelectLabelGroupModal(false)
    inputSearchWord("")
    if (login.permissions?.high_volume) {
      setLabelGroupList([])
      setLabelGroupListInit([])
    }
  }, [exportModalState.display])

  useEffect(() => {
    if (!inputLabelGroupId) return
    if (fileType === "project") {
      updateLabelGroupId(inputLabelGroupId)
    }
  }, [fileType])

  useEffect(() => {
    setLabelGroupList(login.permissions?.high_volume ? [] : task.taskExport?.label_groups || [])
    setLabelGroupListInit(login.permissions?.high_volume ? [] : task.taskExport?.label_groups || [])
  }, [task.taskExport])

  useEffect(() => {
    if (!labelGroupListInit.length && !login.permissions?.high_volume) {
      return undefined
    }
    setIsSearched(false)
    const timer = setTimeout(() => {
      if (!composition) {
        searchLabelGroupList()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchLabelGroupWord, composition])

  return (
    <Modal
      title="データエクスポート"
      show={displayModal}
      wrapperClass="export_data"
      closeCallback={closeCallback}
      closeable
    >
      {
        commonPart.payment_finished ? (
          <>
            <div>
              {
                login.permissions?.task_report_view && (
                  <>
                    <button
                      type="button"
                      onClick={() => setFileType("project")}
                      style={{
                        backgroundColor: fileType === "project" ? "#666666" : "#EEEEEE",
                        color: fileType === "project" ? "#FFFFFF" : "#666666",
                        cursor: "pointer",
                      }}
                    >
                      プロジェクト別作業データ詳細
                    </button>
                  </>
                )
              }
              {
                login.permissions?.task_report_view && (
                  <>
                    <button
                      type="button"
                      onClick={() => setFileType("project_all")}
                      style={{
                        backgroundColor: fileType === "project_all" ? "#666666" : "#EEEEEE",
                        color: fileType === "project_all" ? "#FFFFFF" : "#666666",
                        cursor: "pointer",
                      }}
                    >
                      プロジェクトデータ一括
                    </button>
                  </>
                )
              }
              {
                login.permissions?.task_category_report_view && (
                  <>
                    <button
                      type="button"
                      onClick={() => setFileType("category")}
                      style={{
                        backgroundColor: fileType === "category" ? "#666666" : "#EEEEEE",
                        color: fileType === "category" ? "#FFFFFF" : "#666666",
                        cursor: "pointer",
                      }}
                    >
                      カテゴリーデータ
                    </button>
                  </>
                )
              }
              {
                login.permissions?.task_attendance_export && (
                  <>
                    <button
                      type="button"
                      onClick={() => setFileType("attendance")}
                      style={{
                        backgroundColor: fileType === "attendance" ? "#666666" : "#EEEEEE",
                        color: fileType === "attendance" ? "#FFFFFF" : "#666666",
                        cursor: "pointer",
                      }}
                    >
                      勤怠データ
                    </button>
                  </>
                )
              }
            </div>
            <div
              style={{
                border: "solid 1px #666666",
                padding: "16px",
              }}
            >
              {
                fileType === "project" && (
                  <>
                    <div className="item">
                      <div className="title">
                        出力するプロジェクトを選択してください
                      </div>
                      <div className="detail row_1">
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderRadius: "3px",
                              padding: "12px 8px",
                              fontSize: "12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "4px",
                              width: "100%",
                              height: "40px",
                              position: "relative",
                              cursor: "pointer",
                              color: selectLabelGroup !== undefined ? "#333333" : "#CCCCCC",
                            }}
                            onClick={() => setIsDisplaySelectLabelGroupModal(true)}
                            role="none"
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {
                                !!selectLabelGroup && (
                                  <div
                                    style={{
                                      backgroundColor: selectLabelGroup.label_group_color,
                                      width: "12px",
                                      height: "12px",
                                    }}
                                  />
                                )
                              }
                              {selectLabelGroup?.label_group_name || "プロジェクトを選択してください"}
                            </div>
                            <FontAwesomeIcon
                              icon={['fas', 'angle-down']}
                              style={{
                                fontSize: "20px",
                                color: "#666666",
                              }}
                            />
                          </div>
                          {
                            isDisplaySelectLabelGroupModal && (
                              <>
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "30px",
                                    left: 0,
                                    backgroundColor: "#FFFFFF",
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    width: "100%",
                                    zIndex: "2004",
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      padding: "8px 16px",
                                      backgroundColor: "#EEEEEE",
                                      borderRadius: "4px 4px 0 0",
                                    }}
                                  >
                                    出力するプロジェクトを選択
                                  </div>
                                  <div
                                    style={{
                                      padding: "16px",
                                    }}
                                  >
                                    <div
                                      className="search_area"
                                      style={{
                                        position: "relative",
                                        width: "100%",
                                        marginBottom: "8px",
                                        backgroundColor: "#FFFFFF",
                                        border: "solid 1px #CCCCCC",
                                        padding: "0 8px",
                                        borderRadius: "4px",
                                        fontSize: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={['fas', 'search']}
                                        className="icon"
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          cursor: "initial",
                                        }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="プロジェクトを検索"
                                        onChange={(event) => inputSearchWord(event.target.value)}
                                        onKeyDown={(event) => keyDown(event)}
                                        onCompositionStart={() => setComposition(true)}
                                        onCompositionEnd={() => setComposition(false)}
                                        value={searchLabelGroupWord}
                                        style={{
                                          width: "100%",
                                          fontSize: "12px",
                                          border: "none",
                                          backgroundColor: "transparent",
                                          padding: "8px 16px",
                                        }}
                                      />
                                      {
                                        searchLabelGroupWord && (
                                          <button type="button" className="reset" onClick={resetLabelGroup}>
                                            <FontAwesomeIcon icon={['far', 'times-circle']} />
                                          </button>
                                        )
                                      }
                                    </div>
                                    {
                                      isWordLoading ? (
                                        <LoadingSquare>
                                          <>
                                            プロジェクトデータを取得しています…
                                          </>
                                        </LoadingSquare>
                                      ) : (
                                        <>
                                          {
                                            !labelGroupList.length ? (
                                              <>
                                                <div
                                                  style={{
                                                    padding: "16px",
                                                    backgroundColor: "#f5f7ff",
                                                    textAlign: "center",
                                                    fontSize: "16px",
                                                    color: "#999999",
                                                  }}
                                                >
                                                  {
                                                    searchLabelGroupWord.length ? (
                                                      <>
                                                        {
                                                          isSearched ? (
                                                            "キーワードに該当するプロジェクトがありませんでした"
                                                          ) : (
                                                            "検索中…"
                                                          )
                                                        }
                                                      </>
                                                    ) : (
                                                      "キーワードを入力して、プロジェクトを検索してください"
                                                    )
                                                  }
                                                </div>
                                              </>
                                            ) : (
                                              <>
                                                {
                                                  login.permissions?.high_volume && labelGroupList.length === SEARCH_LIMIT && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        flexFlow: "column",
                                                        alignItems: "center",
                                                        gap: "4px",
                                                        border: "solid 1px #FF0000",
                                                        borderRadius: "4px",
                                                        backgroundColor: "#fff2f2",
                                                        color: "#FF0000",
                                                        padding: "8px",
                                                        marginBottom: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        {`検索上位${SEARCH_LIMIT}件が表示されています`}
                                                      </div>
                                                      <div>
                                                        お探しのプロジェクトが見つからない時は、キーワードを詳細に入力してください
                                                      </div>
                                                    </div>
                                                  )
                                                }
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    gap: "4px",
                                                    flexWrap: "wrap",
                                                    fontSize: "10px",
                                                    maxHeight: "400px",
                                                    overflowY: "scroll",
                                                    padding: "8px",
                                                  }}
                                                >
                                                  {
                                                    labelGroupList.map((v) => (
                                                      <div
                                                        key={v.label_group_id}
                                                        onClick={() => clickLabelGroup(v)}
                                                        role="none"
                                                        style={{
                                                          backgroundColor: "#FFFFFF",
                                                          border: "solid 1px #CCCCCC",
                                                          borderRadius: "20px",
                                                          padding: "4px 8px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "4px",
                                                          cursor: "pointer",
                                                        }}
                                                      >
                                                        <div
                                                          style={{
                                                            backgroundColor: v.label_group_color,
                                                            width: "12px",
                                                            height: "12px",
                                                          }}
                                                        />
                                                        <div>
                                                          {v.label_group_name}
                                                          {v.label_group_code && `(${v.label_group_code})`}
                                                        </div>
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </>
                                            )
                                          }
                                        </>
                                      )
                                    }
                                  </div>
                                </div>
                                <div
                                  className="close_screen gray"
                                  role="presentation"
                                  onClick={() => {
                                    setIsDisplaySelectLabelGroupModal(false)
                                    inputSearchWord("")
                                  }}
                                />
                              </>
                            )
                          }
                        </div>
                      </div>
                    </div>
                  </>
                )
              }
              <div className="item">
                <div className="title">
                  {t('export.select_month.title')}
                </div>
                <div className="detail row_1">
                  <div className="calendar_text">
                    <button
                      type="button"
                      className="display button in_modal"
                      onClick={() => openCalendarModal()}
                    >
                      {displayDate}
                      <FontAwesomeIcon icon={['fas', 'angle-down']} className={`in_modal ${isCalendarDisplay && 'open'}`} />
                    </button>
                  </div>
                  <div className={`calendar_box in_modal ${isCalendarDisplay || 'none'}`}>
                    <div className="range">
                      <div className="text">
                        {t(`range.calendar.header_text.monthly`)}
                      </div>
                    </div>
                    <div className="monthly">
                      <div className="select_month">
                        <div>
                          <button
                            type="button"
                            onClick={() => setTargetYear(targetYear - 1)}
                            disabled={Number(format((commonPart.company?.created_at), 'YYYY')) >= targetYear}
                          >
                            <FontAwesomeIcon icon={['fas', 'angle-left']} className="in_modal" />
                          </button>
                        </div>
                        <div>
                          {`${targetYear}${t('time.time.short.year')}`}
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => setTargetYear(targetYear + 1)}
                            disabled={Number(format((nowDate()), 'YYYY')) <= targetYear}
                          >
                            <FontAwesomeIcon icon={['fas', 'angle-right']} className="in_modal" />
                          </button>
                        </div>
                      </div>
                      {
                        month.map((v, i) => {
                          const target = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
                          const isClickableMonth = Number(format((commonPart.company?.created_at), 'YYYYMM')) <= Number(target)
                            && Number(format((nowDate()), 'YYYYMM')) >= Number(target)
                          const isSelected = targetDate === target
                          return (
                            <div className="monthly_button" key={v}>
                              <button
                                type="button"
                                className={isSelected ? "selected" : ""}
                                onClick={() => handleChangeMonth(target)}
                                disabled={!isClickableMonth}
                              >
                                {`${v}${t('time.time.short.month')}`}
                              </button>
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                  <div className={`screen in_modal transparent ${isCalendarDisplay || 'none'}`} role="presentation" onClick={closeCalendarModal} />
                </div>
              </div>
              <div className="item">
                <div className="title">
                  {t('export.character_code.title')}
                  <p className="annotation">
                    {t('export.character_code.annotation')}
                  </p>
                </div>
                <div className="detail row_1 code">
                  <label htmlFor="code_1">
                    <input
                      type="radio"
                      name="code"
                      id="code_1"
                      value="Shift-JIS"
                      defaultChecked
                      onClick={(e) => setInputCode(e.currentTarget.value)}
                    />
                    Shift-JIS
                  </label>
                  <label htmlFor="code_2">
                    <input
                      type="radio"
                      name="code"
                      id="code_2"
                      value="UTF-8"
                      onClick={(e) => setInputCode(e.currentTarget.value)}
                    />
                    UTF-8
                  </label>
                </div>
                {
                  fileType === "attendance" && (
                    <>
                      <div className="data_title">
                        <div className="title">{t('export.approved_or_all.title')}</div>
                      </div>
                      <div className="detail row_2 filter">
                        <label htmlFor="filter_approved">
                          <input
                            type="radio"
                            name="filter"
                            id="filter_approved"
                            value="true"
                            defaultChecked
                            onClick={() => setInputIsApproved(true)}
                          />
                          承認済みのみ
                        </label>
                        <label htmlFor="filter_all">
                          <input
                            type="radio"
                            name="filter"
                            id="filter_all"
                            value="false"
                            onClick={() => setInputIsApproved(false)}
                          />
                          全て
                        </label>
                      </div>
                    </>
                  )
                }
                {
                  fileType === "category" && (
                    <>
                      <div className="data_title">
                        <div className="title">
                          出力タイプ
                        </div>
                      </div>
                      <div className="detail row_1">
                        <select value={exportType} onChange={(e) => setExportType(e.target.value)}>
                          <option value="label_group">
                            プロジェクト別
                          </option>
                          <option value="label">
                            プロジェクト・業務別
                          </option>
                          <option value="member">
                            プロジェクト・業務・メンバー別
                          </option>
                          <option value="date">
                            プロジェクト・業務・メンバー・日付別
                          </option>
                        </select>
                      </div>
                    </>
                  )
                }
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              トライアル期間中はご利用になれません
            </div>
            <div
              style={{
                marginTop: "16px",
                textAlign: "center",
              }}
            >
              出力できるデータに関する情報は、
              <span
                role="none"
                onClick={() => clickZendeskLink("https://qasee.zendesk.com/hc/ja/articles/43679918012697")}
                style={{
                  color: "#337ab7",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                こちら
              </span>
              をご参照ください。
            </div>
          </>
        )
      }
      {
        commonPart.payment_finished ? (
          <>
            <button type="button" className="button cancel" onClick={closeCallback}>
              {t('setting.personal.custom_alert.modal.button.cancel')}
            </button>
            <button
              type="button"
              className="button"
              onClick={saveExport}
              disabled={fileType === "project" && !selectLabelGroup}
            >
              {t('export.button.submit')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="button cancel" onClick={closeCallback}>
              閉じる
            </button>
          </>
        )
      }
    </Modal>
  )
}

export default ExportModal
